import React, { useState, useEffect, useRef } from 'react';
import { X, Search, Plus, Trash2, Save, Loader2, Download, Upload, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { API_ENDPOINTS } from '../config';

/**
 * DataNotesModal - View and edit data notes (data dictionary) for a datasource
 *
 * Features:
 * - View all data notes with hierarchical structure (Database → Table → Column → Value)
 * - Live search/filter across all fields
 * - Edit note and required fields
 * - Add/delete rows
 * - Bulk save changes
 * - Read-only for org-level notes (non-admins)
 */
function DataNotesModal({ isOpen, onClose, datasourceName, datasource, onDataNotesUpdated }) {
  const [dataNotes, setDataNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [modifiedNotes, setModifiedNotes] = useState(new Set()); // Track which notes were modified
  const [importing, setImporting] = useState(false);
  const [importSuccess, setImportSuccess] = useState(false);
  const [importMessage, setImportMessage] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [refreshSuccess, setRefreshSuccess] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState('');
  const fileInputRef = useRef(null);
  const [editingNote, setEditingNote] = useState(null); // Track which note is being edited in modal

  // Local datasource detection
  const [isLocalDatasource, setIsLocalDatasource] = useState(false);
  const [localDatasourceInfo, setLocalDatasourceInfo] = useState(null);
  const [showSqlQuery, setShowSqlQuery] = useState(false);

  // Fetch data notes when modal opens
  useEffect(() => {
    if (isOpen && datasourceName) {
      fetchDataNotes();
      checkIfLocalDatasource();
    }
  }, [isOpen, datasourceName]);

  // Check if datasource is local and get SQL query if needed
  const checkIfLocalDatasource = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_ENDPOINTS.main}/data-notes/${encodeURIComponent(datasourceName)}/local-info`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setIsLocalDatasource(response.data.is_local);
      if (response.data.is_local) {
        setLocalDatasourceInfo(response.data);
      }
    } catch (error) {
      console.error('Failed to check if datasource is local:', error);
      setIsLocalDatasource(false);
    }
  };

  // Close edit modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && editingNote) {
        setEditingNote(null);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [editingNote]);

  const fetchDataNotes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_ENDPOINTS.main}/data-notes/${encodeURIComponent(datasourceName)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDataNotes(response.data.data_notes || []);
      setModifiedNotes(new Set());
    } catch (error) {
      console.error('Failed to fetch data notes:', error);
      alert('Failed to load data notes');
    } finally {
      setLoading(false);
    }
  };

  const handleNoteChange = (noteId, field, value) => {
    setDataNotes(prev => prev.map(note =>
      note.id === noteId ? { ...note, [field]: value } : note
    ));
    setModifiedNotes(prev => new Set([...prev, noteId]));
  };

  const handleSave = async () => {
    // If no modifications, just close the modal
    if (modifiedNotes.size === 0) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const updates = dataNotes
        .filter(note => modifiedNotes.has(note.id) && note.is_editable)
        .map(note => ({
          id: note.id,
          note: note.note,
          required: note.required
        }));

      if (updates.length === 0) {
        onClose();
        return;
      }

      await axios.put(
        `${API_ENDPOINTS.main}/data-notes/${encodeURIComponent(datasourceName)}`,
        { updates },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setModifiedNotes(new Set());
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000); // Hide after 3 seconds

      // Clear Taurus schema cache (fire-and-forget, no blocking)
      try {
        fetch(`${API_ENDPOINTS.taurus}/api/clear-schema-cache?datasource_name=${encodeURIComponent(datasourceName)}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => {}); // Silently fail if Taurus not running
        console.log('[Taurus] Schema cache cleared for datasource:', datasourceName);
      } catch (err) {
        // Silently fail if Taurus not available
        console.log('[Taurus] Failed to clear schema cache (server may not be running)');
      }

      // Notify parent to refresh sample questions
      if (onDataNotesUpdated) {
        onDataNotesUpdated();
      }

      // Close the modal after successful save
      onClose();
    } catch (error) {
      console.error('Failed to save data notes:', error);
      alert('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this row?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_ENDPOINTS.main}/data-notes/${encodeURIComponent(datasourceName)}/row/${noteId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setDataNotes(prev => prev.filter(note => note.id !== noteId));
      setDeleteSuccess(true);
      setTimeout(() => setDeleteSuccess(false), 3000); // Hide after 3 seconds
    } catch (error) {
      console.error('Failed to delete row:', error);
      alert('Failed to delete row');
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_ENDPOINTS.main}/data-notes/${encodeURIComponent(datasourceName)}/export`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `data_notes_${datasourceName}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export data notes:', error);
      alert('Failed to export data notes');
    }
  };

  const handleImportClick = () => {
    // Check if datasource is editable
    if (datasource && !datasource.is_editable) {
      alert('Cannot import data notes for shared datasources. Only the owner can modify shared datasource data notes.');
      return;
    }
    fileInputRef.current?.click();
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API_ENDPOINTS.main}/data-notes/${encodeURIComponent(datasourceName)}/import`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setImportSuccess(true);
      setImportMessage(response.data.message || 'Import successful');
      setTimeout(() => {
        setImportSuccess(false);
        setImportMessage('');
      }, 5000);

      // Refresh data notes
      await fetchDataNotes();

      // Notify parent to refresh sample questions
      if (onDataNotesUpdated) {
        onDataNotesUpdated();
      }
    } catch (error) {
      console.error('Failed to import data notes:', error);
      alert(error.response?.data?.detail || 'Failed to import data notes');
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRefresh = async () => {
    // Warn user about destructive operation
    const confirmed = window.confirm(
      'Refresh Schema will re-scan the database and replace all existing notes.\n\n' +
      '⚠️ Warning: Any descriptions or notes you\'ve added will be lost.\n\n' +
      'Use this when you\'ve added new tables or columns to your database.\n\n' +
      'Continue?'
    );

    if (!confirmed) return;

    setRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_ENDPOINTS.main}/data-notes/${encodeURIComponent(datasourceName)}/refresh`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setRefreshSuccess(true);
      setRefreshMessage(response.data.message || `Refreshed: ${response.data.notes_count} notes`);
      setTimeout(() => {
        setRefreshSuccess(false);
        setRefreshMessage('');
      }, 5000);

      // Refresh data notes display
      await fetchDataNotes();

      // Clear Taurus schema cache
      try {
        fetch(`${API_ENDPOINTS.taurus}/api/clear-schema-cache?datasource_name=${encodeURIComponent(datasourceName)}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => {});
      } catch (err) {
        // Silently fail if Taurus not available
      }

      // Notify parent to refresh sample questions
      if (onDataNotesUpdated) {
        onDataNotesUpdated();
      }
    } catch (error) {
      console.error('Failed to refresh data notes:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to refresh data notes';
      alert(errorMsg);
    } finally {
      setRefreshing(false);
    }
  };

  // Filter data notes based on search query
  const filteredNotes = dataNotes.filter(note => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      (note.table_name || '').toLowerCase().includes(query) ||
      (note.column_name || '').toLowerCase().includes(query) ||
      (note.data_type || '').toLowerCase().includes(query) ||
      (note.value || '').toLowerCase().includes(query) ||
      (note.note || '').toLowerCase().includes(query) ||
      (note.note_level || '').toLowerCase().includes(query)
    );
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-7xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Data Notes
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {datasourceName} - {filteredNotes.length} notes
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
              title="Export to CSV"
            >
              <Download className="h-4 w-4" />
              Export
            </button>
            <button
              onClick={handleImportClick}
              disabled={importing || (datasource && !datasource.is_editable)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={
                datasource && !datasource.is_editable
                  ? "Import not allowed for shared datasources"
                  : "Import from CSV"
              }
            >
              {importing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              Import
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleImport}
              className="hidden"
            />
            <button
              onClick={handleRefresh}
              disabled={refreshing || isLocalDatasource || (datasource && !datasource.is_editable)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={
                isLocalDatasource
                  ? "Cannot refresh local datasource from cloud - use CSV import"
                  : datasource && !datasource.is_editable
                  ? "Refresh not allowed for shared datasources"
                  : "Re-scan database schema (replaces existing notes)"
              }
            >
              {refreshing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Refresh
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search tables, columns, notes..."
              className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Local Datasource Helper */}
        {isLocalDatasource && localDatasourceInfo && (
          <div className="mx-4 mt-4 mb-2 bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-300 dark:border-amber-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <div className="h-8 w-8 rounded-full bg-amber-100 dark:bg-amber-800 flex items-center justify-center">
                  <span className="text-lg">🏠</span>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-1">
                  Local Database Detected
                </h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                  {localDatasourceInfo.message}
                </p>

                {!showSqlQuery ? (
                  <button
                    onClick={() => setShowSqlQuery(true)}
                    className="text-sm px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors font-medium"
                  >
                    Show SQL Query Template
                  </button>
                ) : (
                  <div className="space-y-3">
                    <div className="bg-gray-900 dark:bg-gray-950 rounded-lg p-4 overflow-x-auto">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-mono text-gray-400">
                          {localDatasourceInfo.database_type.toUpperCase()} Query
                        </span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(localDatasourceInfo.sql_query);
                            alert('SQL query copied to clipboard!');
                          }}
                          className="text-xs px-2 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
                        >
                          Copy
                        </button>
                      </div>
                      <pre className="text-xs text-green-400 font-mono overflow-x-auto">
                        {localDatasourceInfo.sql_query}
                      </pre>
                    </div>

                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                      <p className="text-sm text-gray-700 dark:text-gray-300 font-medium mb-2">
                        📋 Instructions:
                      </p>
                      <ol className="text-sm text-gray-600 dark:text-gray-400 space-y-1 ml-4 list-decimal">
                        <li>Run the SQL query above in your local {localDatasourceInfo.database_type} database</li>
                        <li>Export the results as CSV</li>
                        <li>Click "Import" button above to upload the CSV</li>
                        <li>Add descriptions and domain knowledge to complete your Data Notes</li>
                      </ol>
                    </div>

                    <button
                      onClick={() => setShowSqlQuery(false)}
                      className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                    >
                      Hide SQL Query
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : filteredNotes.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              {searchQuery ? 'No notes match your search' : 'No data notes found'}
            </div>
          ) : (
            <>
              {/* Mobile card list */}
              <div className="sm:hidden space-y-3">
                {filteredNotes.map((note) => (
                  <div
                    key={note.id}
                    className={`border rounded-lg p-3 ${modifiedNotes.has(note.id) ? 'border-yellow-400 bg-yellow-50 dark:bg-yellow-900/10' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'}`}
                  >
                    {/* Top row: level badge + required + delete */}
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium px-2 py-1 rounded bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                        {note.note_level}
                      </span>
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                          <input
                            type="checkbox"
                            checked={note.required === '1'}
                            onChange={(e) => handleNoteChange(note.id, 'required', e.target.checked ? '1' : '0')}
                            disabled={!note.is_editable}
                            className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500 disabled:cursor-not-allowed"
                          />
                          Required
                        </label>
                        <button
                          onClick={() => handleDelete(note.id)}
                          disabled={!note.is_editable}
                          className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:cursor-not-allowed"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    {/* Table / Column / Type */}
                    {(note.table_name || note.column_name) && (
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mb-2 text-xs text-gray-500 dark:text-gray-400">
                        {note.table_name && <span><span className="font-medium text-gray-700 dark:text-gray-300">Table:</span> {note.table_name}</span>}
                        {note.column_name && <span><span className="font-medium text-gray-700 dark:text-gray-300">Column:</span> {note.column_name}</span>}
                        {note.data_type && <span><span className="font-medium text-gray-700 dark:text-gray-300">Type:</span> {note.data_type}</span>}
                      </div>
                    )}
                    {/* Note button — full width, easy to tap */}
                    <button
                      onClick={() => note.is_editable && setEditingNote(note)}
                      disabled={!note.is_editable}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-white text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:cursor-not-allowed focus:outline-none focus:ring-1 focus:ring-blue-500"
                      title={note.is_editable ? "Tap to edit" : "Read-only"}
                    >
                      {note.note
                        ? <span className="line-clamp-3">{note.note}</span>
                        : <span className="text-gray-400">Add description...</span>
                      }
                    </button>
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Level</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Table</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Column</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Type</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Value</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Note</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Required</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredNotes.map((note) => (
                      <tr
                        key={note.id}
                        className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${
                          modifiedNotes.has(note.id) ? 'bg-yellow-50 dark:bg-yellow-900/10' : ''
                        }`}
                      >
                        <td className="px-3 py-2 text-gray-900 dark:text-white whitespace-nowrap">
                          <span className="text-xs font-medium px-2 py-1 rounded bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                            {note.note_level}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-900 dark:text-white">{note.table_name || '-'}</td>
                        <td className="px-3 py-2 text-gray-900 dark:text-white">{note.column_name || '-'}</td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400 text-xs">{note.data_type || '-'}</td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400 text-xs max-w-xs truncate">{note.value || '-'}</td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => note.is_editable && setEditingNote(note)}
                            disabled={!note.is_editable}
                            className="w-full px-2 py-1 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-white text-xs text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:cursor-not-allowed focus:outline-none focus:ring-1 focus:ring-blue-500 max-h-32 overflow-hidden relative group"
                            title={note.is_editable ? "Click to edit" : "Read-only: Published by your organization"}
                          >
                            <div className="line-clamp-[7]">
                              {note.note || <span className="text-gray-400">Add description...</span>}
                            </div>
                            {note.note && note.note.length > 100 && (
                              <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white dark:from-gray-900 to-transparent flex items-end justify-center pb-0.5">
                                <span className="text-[10px] text-blue-600 dark:text-blue-400 font-medium">
                                  Click to view full note...
                                </span>
                              </div>
                            )}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={note.required === '1'}
                            onChange={(e) => handleNoteChange(note.id, 'required', e.target.checked ? '1' : '0')}
                            disabled={!note.is_editable}
                            className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500 disabled:cursor-not-allowed"
                            title={note.is_editable ? "Toggle required" : "Read-only: Published by your organization"}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => handleDelete(note.id)}
                            disabled={!note.is_editable}
                            className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:cursor-not-allowed"
                            title={note.is_editable ? "Delete row" : "Read-only: Published by your organization"}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="text-sm">
            {modifiedNotes.size > 0 && !saveSuccess && !deleteSuccess && !importSuccess && (
              <span className="text-yellow-600 dark:text-yellow-400 font-medium">
                {modifiedNotes.size} unsaved change{modifiedNotes.size !== 1 ? 's' : ''}
              </span>
            )}
            {saveSuccess && (
              <span className="text-green-600 dark:text-green-400 font-medium animate-pulse">
                ✓ Changes saved successfully!
              </span>
            )}
            {deleteSuccess && (
              <span className="text-green-600 dark:text-green-400 font-medium animate-pulse">
                ✓ Row deleted successfully!
              </span>
            )}
            {importSuccess && (
              <span className="text-green-600 dark:text-green-400 font-medium animate-pulse">
                ✓ {importMessage}
              </span>
            )}
            {refreshSuccess && (
              <span className="text-green-600 dark:text-green-400 font-medium animate-pulse">
                ✓ {refreshMessage}
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Close
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : modifiedNotes.size === 0 ? (
                <>
                  <Save className="h-4 w-4" />
                  Close
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save and Close
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Edit Note Modal */}
      {editingNote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  Edit Note
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {editingNote.table_name && editingNote.column_name
                    ? `${editingNote.table_name}.${editingNote.column_name}`
                    : editingNote.table_name || 'Database note'}
                </p>
              </div>
              <button
                onClick={() => setEditingNote(null)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6 space-y-4">
              {/* Context Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg text-sm">
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Level:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{editingNote.note_level}</span>
                </div>
                {editingNote.data_type && (
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Type:</span>
                    <span className="ml-2 text-gray-900 dark:text-white">{editingNote.data_type}</span>
                  </div>
                )}
                {editingNote.value && (
                  <div className="col-span-2">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Value:</span>
                    <span className="ml-2 text-gray-900 dark:text-white">{editingNote.value}</span>
                  </div>
                )}
              </div>

              {/* Note Editor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description / Note
                </label>
                <textarea
                  value={editingNote.note || ''}
                  onChange={(e) => {
                    setEditingNote({ ...editingNote, note: e.target.value });
                    handleNoteChange(editingNote.id, 'note', e.target.value);
                  }}
                  placeholder={
                    editingNote.note_level === 'Sample'
                      ? 'Enter sample questions separated by semicolons (e.g., Question 1; Question 2; Question 3)'
                      : editingNote.note_level === 'Db_File'
                      ? 'Add a description of this database/file...'
                      : editingNote.note_level === 'Table'
                      ? 'Describe what this table contains and when to use it...'
                      : editingNote.note_level === 'Column'
                      ? 'Explain what this column represents and any important details...'
                      : 'Add a detailed description of this data element...'
                  }
                  rows={8}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
                {editingNote.note_level === 'Sample' ? (
                  <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                    <p className="text-xs font-medium text-blue-800 dark:text-blue-300 mb-1">
                      💡 Sample Questions Format
                    </p>
                    <p className="text-xs text-blue-700 dark:text-blue-400 mb-2">
                      Separate questions with semicolons (;). Works with or without quotes.
                    </p>
                    <div className="text-xs font-mono text-blue-600 dark:text-blue-400 space-y-1">
                      <div>✓ Simple: <span className="text-gray-700 dark:text-gray-300">What is the average price?; Show top 10 products; Create a sales chart</span></div>
                      <div>✓ With quotes: <span className="text-gray-700 dark:text-gray-300">"Question 1"; "Question 2"; "Question 3"</span></div>
                      <div>✓ Named: <span className="text-gray-700 dark:text-gray-300">Q1: First question; Q2: Second question</span></div>
                    </div>
                  </div>
                ) : (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {editingNote.note_level === 'Db_File'
                      ? 'Provide an overview of this database, its purpose, and general structure.'
                      : editingNote.note_level === 'Table'
                      ? 'Explain what this table stores, typical use cases, and any relationships with other tables.'
                      : editingNote.note_level === 'Column'
                      ? 'Describe what values this column contains, any formatting rules, and how it should be interpreted.'
                      : 'This description helps users understand what this data represents and how to use it.'}
                  </p>
                )}
              </div>

              {/* Required Checkbox */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="required-checkbox"
                  checked={editingNote.required === '1'}
                  onChange={(e) => {
                    const newValue = e.target.checked ? '1' : '0';
                    setEditingNote({ ...editingNote, required: newValue });
                    handleNoteChange(editingNote.id, 'required', newValue);
                  }}
                  className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <label htmlFor="required-checkbox" className="text-sm text-gray-700 dark:text-gray-300">
                  Mark as required field
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setEditingNote(null)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => setEditingNote(null)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataNotesModal;
